'use client';

import { useEffect } from 'react';
import { useRouter, usePathname, useSearchParams } from 'next/navigation';
import { useAuthStore, useUser } from '@/features/auth';
import { LanguageSelector } from '@/features/i18n/ui/LanguageSelector';
import { createLogger } from '@/shared/lib/logger';

const logger = createLogger('AuthLayout');

// Auth pages that should remain accessible even when logged in
const BYPASS_AUTH_REDIRECT_PATHS = ['/auth/reset-password'];

export default function AuthLayout({
  children,
}: {
  children: React.ReactNode;
}) {
  const user = useUser();
  const loading = useAuthStore(
    (state) => state.isLoggingIn || state.isRegistering
  );
  const isInitialized = useAuthStore((state) => state.isInitialized);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const pendingEmailVerification = useAuthStore(
    (state) => state.pendingEmailVerification
  );
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  // /auth/login?email=... is the user-switching flow — LoginForm logs the
  // current user out when the typed email differs from the stored one. The
  // redirect here would short-circuit that, so let the page render.
  const isLoginWithEmail =
    pathname === '/auth/login' && searchParams.has('email');

  const shouldBypassRedirect =
    isLoginWithEmail ||
    BYPASS_AUTH_REDIRECT_PATHS.some((path) => pathname.startsWith(path));

  // Login is the only auth page on the teal (bg-primary) background;
  // all the others render on bg-background (white/neutral).
  const isTealAuthBackground = pathname.startsWith('/auth/login');

  useEffect(() => {
    // Only redirect after auth is fully initialized and not loading
    // Don't redirect if email verification is pending
    // Don't redirect on pages that should remain accessible when logged in
    if (
      isInitialized &&
      !loading &&
      user &&
      isAuthenticated &&
      !pendingEmailVerification &&
      !shouldBypassRedirect
    ) {
      logger.info('User authenticated, redirecting to dashboard');
      router.replace('/');
    }
  }, [
    user,
    loading,
    router,
    isInitialized,
    isAuthenticated,
    pendingEmailVerification,
    shouldBypassRedirect,
  ]);

  // Already-authenticated visitors get a full-screen overlay while the
  // redirect effect above fires, instead of an early return that unmounts
  // the page.
  const isRedirectingAuthenticated =
    isInitialized &&
    !!user &&
    isAuthenticated &&
    !pendingEmailVerification &&
    !shouldBypassRedirect;

  // IMPORTANT: children must always render. `isInitialized` is false during
  // SSR (auth lives in localStorage), so an early `return null` here made
  // every /auth/* page ship an EMPTY server-rendered <body>. Bots that don't
  // execute JS (Paddle domain verification, search engines) then saw a blank
  // page and Paddle rejected the domain as "offline or under construction".
  // Auth gating happens after hydration via the redirect effect + overlay.
  return (
    <>
      <div className="fixed top-4 right-4 z-50 w-44">
        <LanguageSelector
          triggerClassName={
            isTealAuthBackground
              ? 'bg-white/15 hover:bg-white/25 border-white/30 text-white backdrop-blur-sm shadow-none transition-colors'
              : undefined
          }
        />
      </div>
      {children}
      {isRedirectingAuthenticated && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center bg-background">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
        </div>
      )}
    </>
  );
}
